import {
  postStorageSearch,
  postStorageSync,
  postStorageLogAction,
  postGetAllWarehouses,
} from '../../util/api';
import { types as sdkTypes } from '../../util/sdkLoader';
import { STORAGE_STATUS_TERMINATED } from '../../util/types';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SEARCH_STORAGE_REQUEST = 'app/ManageStoragePage/SEARCH_STORAGE_REQUEST';
export const SEARCH_STORAGE_SUCCESS = 'app/ManageStoragePage/SEARCH_STORAGE_SUCCESS';
export const SEARCH_STORAGE_ERROR = 'app/ManageStoragePage/SEARCH_STORAGE_ERROR';

export const LOAD_WAREHOUSES_SUCCESS = 'app/ManageStoragePage/LOAD_WAREHOUSES_SUCCESS';
export const LOAD_WAREHOUSES_ERROR = 'app/ManageStoragePage/LOAD_WAREHOUSES_ERROR';

export const SET_STORAGE_FILTERS = 'app/ManageStoragePage/SET_STORAGE_FILTERS';

// ================ Reducer ================ //

const initialState = {
  items: [],
  itemsLoading: false,
  itemsError: null,
  warehouses: [],
  filters: {},
};

const manageStoragePageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_STORAGE_REQUEST:
      return { ...state, itemsLoading: true, itemsError: null };
    case SEARCH_STORAGE_SUCCESS:
      return { ...state, items: payload, itemsLoading: false };
    case SEARCH_STORAGE_ERROR:
      return { ...state, itemsError: payload, itemsLoading: false };

    case LOAD_WAREHOUSES_SUCCESS:
      return { ...state, warehouses: payload };
    case LOAD_WAREHOUSES_ERROR:
      return { ...state, warehouses: [] };

    case SET_STORAGE_FILTERS:
      return { ...state, filters: payload };

    default:
      return state;
  }
};

export default manageStoragePageReducer;

// ================ Action creators ================ //

export const searchStorageRequest = () => ({
  type: SEARCH_STORAGE_REQUEST,
});

export const searchStorageSuccess = items => ({
  type: SEARCH_STORAGE_SUCCESS,
  payload: items,
});

export const searchStorageError = error => ({
  type: SEARCH_STORAGE_ERROR,
  payload: error,
});

export const loadWarehousesSuccess = warehouses => ({
  type: LOAD_WAREHOUSES_SUCCESS,
  payload: warehouses,
});

export const loadWarehousesError = error => ({
  type: LOAD_WAREHOUSES_ERROR,
  payload: error,
});

export const setStorageFilters = filters => ({
  type: SET_STORAGE_FILTERS,
  payload: filters,
});

// ================ Thunks ================ //

export const searchStorage = (filters = {}) => async (dispatch, getState) => {
  dispatch(searchStorageRequest());
  dispatch(setStorageFilters(filters));

  try {
    const state = getState();
    const providerUserId = state.user.currentUser?.id?.uuid;

    const body = { providerUserId };
    // Only include filter keys that actually carry a value, so the backend
    // treats missing filters as "no filter" rather than an empty match.
    if (filters.warehouseId) {
      body.warehouseId = filters.warehouseId;
    }
    if (filters.storageType) {
      body.storageType = filters.storageType;
    }
    if (filters.storageStatus) {
      body.storageStatus = filters.storageStatus;
    }
    if (filters.q) {
      body.q = filters.q;
    }

    const response = await postStorageSearch(body);
    dispatch(searchStorageSuccess(response?.data || []));
  } catch (e) {
    console.error('Storage search failed', e);
    dispatch(searchStorageError(e));
  }
};

export const loadWarehouses = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const userId = state.user.currentUser?.id?.uuid;

    const response = await postGetAllWarehouses({ UserId: userId });
    dispatch(loadWarehousesSuccess(response?.data || []));
  } catch (e) {
    console.error('Load warehouses failed', e);
    dispatch(loadWarehousesError(e));
  }
};

/**
 * Write a storage status change. Shared by the Förvaring list and the storage
 * card (P25009-451) so both record a change the same three ways.
 */
export const writeStorageStatus = async (
  sdk,
  { listingId, providerUserId, storageStatus, action, subUserId }
) => {
  // 1) The listing is the single source of truth — write the new status
  //    onto its publicData first (mirrors the convert-to-ad path). The Mongo
  //    index is only a disposable mirror, so it must never be the sole record
  //    of a termination/return. Stamp storageTerminatedAt on "Säg upp".
  const publicData = { storageStatus };
  if (storageStatus === STORAGE_STATUS_TERMINATED) {
    publicData.storageTerminatedAt = new Date().getTime();
  }
  await sdk.ownListings.update({ id: new UUID(listingId), publicData });

  // 2) Mirror the change into the search index.
  await postStorageSync({ listingId, providerUserId, storageStatus });

  // 3) Record the audit-trail action for this change.
  await postStorageLogAction({ listingId, action, ...(subUserId ? { subUserId } : {}) });
};

export const changeStorageStatus = ({
  listingId,
  providerUserId,
  storageStatus,
  action,
}) => async (dispatch, getState, sdk) => {
  try {
    const subUserId = getState().user.currentUser?.subUser?._id;
    await writeStorageStatus(sdk, { listingId, providerUserId, storageStatus, action, subUserId });

    // Re-run the search with the current filters so the table refreshes.
    const state = getState();
    const filters = state.ManageStoragePage.filters || {};
    dispatch(searchStorage(filters));
  } catch (e) {
    console.error('Change storage status failed', e);
    dispatch(searchStorageError(e));
  }
};
